// src/components/management/ProjectSimulator.jsx
import React, { useState, useMemo } from 'react';
import { Check, Zap, SlidersHorizontal, Users, BarChart, AlertTriangle, ShieldCheck, ArrowRight } from 'lucide-react';
import { projectCharacteristics } from '../../data/projectSkillMapping';
import { simulateProjectSuccess } from '../../services/aiAnalysisService';

const ProjectSimulator = ({ userProfiles }) => {
  const [selectedChars, setSelectedChars] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const availableMembers = useMemo(() => 
    Object.values(userProfiles).filter(p => p && p.role !== 'master'), 
    [userProfiles]
  );

  const handleCharToggle = (charId) => {
    setSelectedChars(prev => 
      prev.includes(charId) ? prev.filter(id => id !== charId) : [...prev, charId]
    );
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };
  
  const handleSimulate = () => {
    setIsLoading(true);
    setResult(null);
    const memberProfiles = selectedMembers.map(id => userProfiles[id]);
    const simulationResult = simulateProjectSuccess(selectedChars, memberProfiles);
    setTimeout(() => {
        setResult(simulationResult);
        setIsLoading(false);
    }, 1500);
  };
  
  const ScoreDonut = ({ score }) => {
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 80 ? 'text-green-500' : score >= 60 ? 'text-yellow-500' : 'text-red-500';

    return (
        <div className="relative w-48 h-48">
            <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle className="text-gray-200" strokeWidth="10" stroke="currentColor" fill="transparent" r="40" cx="50" cy="50" />
                <circle className={color} strokeWidth="10" strokeDasharray={circumference} strokeDashoffset={offset}
                    strokeLinecap="round" stroke="currentColor" fill="transparent" r="40" cx="50" cy="50"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 0.5s ease-out' }}
                />
            </svg>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                <span className={`text-4xl font-bold ${color}`}>{score}</span>
                <span className="text-lg text-gray-600">%</span>
                <p className="text-xs text-gray-500">成功確率</p>
            </div>
        </div>
    );
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg border">
      <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
        <BarChart className="w-6 h-6 text-blue-600" />
        プロジェクト成功確率シミュレーター
      </h2>
      <p className="text-sm text-gray-600 mb-6">プロジェクト特性とチーム構成から、成功確率を科学的に予測します。</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><SlidersHorizontal className="w-5 h-5"/>ステップ1: プロジェクトの特性を選択</h3>
            <div className="grid grid-cols-2 gap-2">
              {projectCharacteristics.map(char => (
                <button key={char.id} onClick={() => handleCharToggle(char.id)}
                  className={`p-3 text-sm rounded-lg border-2 text-left transition-all ${selectedChars.includes(char.id) ? 'border-blue-500 bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                >
                  <p className="font-medium">{char.name}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Users className="w-5 h-5"/>ステップ2: チームメンバーを選択</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {availableMembers.map(member => (
                <button key={member.id} onClick={() => handleMemberToggle(member.id)}
                  className={`w-full p-2 rounded-lg flex items-center gap-2 text-left border-2 transition-all ${selectedMembers.includes(member.id) ? 'border-blue-500 bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                >
                  <Check className={`w-5 h-5 p-0.5 rounded-full ${selectedMembers.includes(member.id) ? 'bg-blue-500 text-white' : 'text-gray-300'}`} />
                  <span>{member.username}</span>
                </button>
              ))}
            </div>
          </div>
          <button onClick={handleSimulate} disabled={selectedChars.length === 0 || selectedMembers.length === 0 || isLoading}
            className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isLoading ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> 分析中...</> : <><Zap className="w-5 h-5"/> 成功確率を予測する</>}
          </button>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><ArrowRight className="w-5 h-5"/>ステップ3: AI分析結果</h3>
          {isLoading && <p className="text-center text-gray-600">AIがチームのポテンシャルを分析しています...</p>}
          {!isLoading && result && (
            <div className="space-y-6 text-center animate-fade-in-up">
              <div className="flex justify-center">
                 <ScoreDonut score={result.score} />
              </div>
              <div className="space-y-4 text-left">
                 <div>
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4"/>リスク因子</h4>
                    <ul className="space-y-1 list-disc list-inside text-sm text-gray-800">
                        {result.risks.map((risk, i) => <li key={i}>{risk}</li>)}
                    </ul>
                 </div>
                 <div>
                    <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4"/>推奨される対策（処方箋）</h4>
                    <ul className="space-y-1 list-disc list-inside text-sm text-gray-800">
                        {result.mitigations.map((mit, i) => <li key={i}>{mit}</li>)}
                    </ul>
                 </div>
              </div>
            </div>
          )}
          {!isLoading && !result && <p className="text-center text-gray-500 py-16">特性とメンバーを選択して予測を開始してください。</p>}
        </div>
      </div>
    </div>
  );
};

export default ProjectSimulator;
